import React, { useState, useEffect } from 'react';
import { GridState, NodeType, HoverElement, InteractionState } from '../types';
import { Node } from './GridVisualization/Node';
import { Edge } from './GridVisualization/Edge';
import { Legend } from './GridVisualization/Legend';
import { useDrag } from './GridVisualization/useDrag';
import { ZoomControls } from './GridVisualization/ZoomControls';
import { ContextMenu, type MenuItem } from './common/Menu';
import { useZoom } from './GridVisualization/useZoom';
import { computeLineRent, lmpLookup } from '../powerFlow/transmissionRent';

interface Props {
  gridState: GridState & { solutionValid?: boolean; solutionMessage?: string };
  onNodePositionChange: (nodeId: string, x: number, y: number) => void;
  onEdgeRatingChange: (edgeId: string, rating: number) => void;
  onAddNode: (type: NodeType, x: number, y: number) => void;
  onRemoveNode: (nodeId: string) => void;
  onAddEdge: (fromNodeId: string, toNodeId: string) => void;
  onRemoveEdge: (edgeId: string) => void;
  onRevealInPanel?: (nodeId: string) => void;
  onInteractionEnd?: () => void;
  onOptimizeLayout: () => void;
  interactionState: InteractionState;
  setInteractionState: (state: InteractionState) => void;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  batteryStates?: Map<string, { soc: number; power: number }>;
  selectedBusId?: string | null;
  onBusSelect?: (nodeId: string) => void;
}

/**
 * An open right-click menu: where it was opened (viewport coords) and on what —
 * a node, or the bare canvas at the given canvas coordinates.
 */
interface ContextMenuState {
  show: boolean;
  x: number;
  y: number;
  nodeId: string | null;
  canvasX?: number;
  canvasY?: number;
}

/** The node types the canvas menu can add, in the order they're offered. */
const ADDABLE_NODES: { type: NodeType; label: string; icon: string }[] = [
  { type: 'generator', label: 'Add Generator', icon: '⚡' },
  { type: 'load', label: 'Add Load', icon: '💡' },
  { type: 'bus', label: 'Add Bus', icon: '🚌' },
  { type: 'battery', label: 'Add Battery', icon: '🔋' },
];

function GridVisualization({ 
  gridState, 
  onNodePositionChange, 
  onEdgeRatingChange,
  onAddNode,
  onRemoveNode,
  onAddEdge,
  onRemoveEdge,
  onRevealInPanel,
  onInteractionEnd,
  onOptimizeLayout,
  interactionState,
  setInteractionState,
  hoverElement,
  onElementHover,
  batteryStates,
  selectedBusId,
  onBusSelect
}: Props) {
  const { nodes, edges } = gridState;

  // node id -> LMP, used to price the transmission (congestion) rent per line.
  const lmpByNodeId = lmpLookup(nodes);

  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    show: false,
    x: 0,
    y: 0,
    nodeId: null,
    canvasX: undefined,
    canvasY: undefined
  });

  // Initialize zoom hook first
  const { 
    viewTransform, 
    setViewTransform,
    handleZoomIn, 
    handleZoomOut, 
    handleZoomToFit,
    canZoomIn,
    canZoomOut,
    svgRef: zoomSvgRef
  } = useZoom({ 
    viewBox: { width: 800, height: 600 }
  });

  // Initialize drag hook with zoom state and shared svgRef
  const {
    dragState,
    handleNodeMouseDown,
    handleEdgeMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSVGMouseDown,
    getMousePosition,
  } = useDrag({
    gridState,
    viewTransform,
    setViewTransform,
    svgRef: zoomSvgRef,
    onNodePositionChange,
    onEdgeRatingChange,
    onDragEnd: onInteractionEnd,
  });

  const svgRef = zoomSvgRef;

  // Wrapper function to call zoom-to-fit with current nodes
  const handleZoomToFitClick = () => {
    handleZoomToFit(nodes);
  };

  // Handle different interaction modes for edge adding
  const handleNodeClick = (event: React.MouseEvent<SVGGElement>, nodeId: string) => {
    if (interactionState.mode === 'adding-edge-select-first') {
      setInteractionState({ mode: 'adding-edge', selectedNodeForEdge: nodeId });
      return;
    }
    
    if (interactionState.mode === 'adding-edge') {
      if (interactionState.selectedNodeForEdge !== nodeId) {
        onAddEdge(interactionState.selectedNodeForEdge, nodeId);
        setInteractionState({ mode: 'normal' });
      }
      return;
    }
  };

  const combinedNodeMouseDown = (event: React.MouseEvent<SVGGElement>, nodeId: string) => {
    handleNodeClick(event, nodeId);
    if (interactionState.mode === 'normal') {
      // Select this bus for the LMP curve. Selecting always sets (never
      // toggles) so that dragging a selected node to reposition it doesn't
      // deselect it mid-drag.
      onBusSelect?.(nodeId);
      handleNodeMouseDown(event, nodeId);
    }
  };

  const handleSVGClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (interactionState.mode === 'adding-node' && interactionState.nodeType) {
      const mousePos = getMousePosition(event);
      onAddNode(interactionState.nodeType, mousePos.x, mousePos.y);
      setInteractionState({ mode: 'normal' });
    }
  };

  const handleNodeRightClick = (event: React.MouseEvent<SVGGElement>, nodeId: string) => {
    event.preventDefault();
    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      nodeId,
      canvasX: undefined,
      canvasY: undefined
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu({ show: false, x: 0, y: 0, nodeId: null, canvasX: undefined, canvasY: undefined });
  };

  const handleRemoveNode = () => {
    if (contextMenu.nodeId) {
      onRemoveNode(contextMenu.nodeId);
    }
    handleContextMenuClose();
  };

  const handleRevealInPanel = () => {
    if (contextMenu.nodeId && onRevealInPanel) {
      onRevealInPanel(contextMenu.nodeId);
    }
    handleContextMenuClose();
  };

  const handleStartLine = () => {
    if (contextMenu.nodeId) {
      setInteractionState({
        mode: 'adding-edge',
        selectedNodeForEdge: contextMenu.nodeId,
      });
    }
    handleContextMenuClose();
  };

  // What the open menu offers: the commands for a node, or — on the bare
  // canvas — one "add" command per node type, dropping it where you clicked.
  const contextMenuItems = (): MenuItem[] => {
    if (contextMenu.nodeId) {
      const items: MenuItem[] = [
        { kind: 'action', label: 'Start Line', icon: '➖', onClick: handleStartLine },
      ];
      if (onRevealInPanel) {
        items.push({
          kind: 'action',
          label: 'Reveal in elements panel',
          icon: '🔎',
          onClick: handleRevealInPanel,
        });
      }
      items.push({ kind: 'separator' });
      items.push({ kind: 'action', label: 'Remove', icon: '🗑️', danger: true, onClick: handleRemoveNode });
      return items;
    }

    const { canvasX, canvasY } = contextMenu;
    if (canvasX === undefined || canvasY === undefined) return [];
    return ADDABLE_NODES.map(({ type, label, icon }) => ({
      kind: 'action' as const,
      label,
      icon,
      onClick: () => {
        onAddNode(type, canvasX, canvasY);
        handleContextMenuClose();
      },
    }));
  };

  const handleEdgeRightClick = (event: React.MouseEvent<SVGGElement>, edgeId: string) => {
    event.preventDefault();
    onRemoveEdge(edgeId);
  };

  const handleCanvasRightClick = (event: React.MouseEvent<SVGRectElement>) => {
    event.preventDefault();
    const mousePos = getMousePosition(event);
    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      nodeId: null,
      canvasX: mousePos.x,
      canvasY: mousePos.y
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setInteractionState({ mode: 'normal' });
    }
  };



  return (
    <div style={{ width: '100%', height: '100%', background: '#f9f9f9', overflow: 'hidden', position: 'relative' }}>
      {/* Solution Status */}
      {gridState.solutionValid === false && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          right: '10px',
          zIndex: 10,
          background: '#f44336',
          color: 'white',
          padding: '12px',
          borderRadius: '5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          ⚠️ Power Flow Not Solvable: {gridState.solutionMessage}
        </div>
      )}
      
      {/* Instructions */}
      <Legend interactionState={interactionState} />

      <svg 
        ref={svgRef}
        width="100%" 
        height="100%" 
        viewBox="0 0 800 600"
        onMouseDown={handleSVGMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleSVGClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{ 
          cursor: dragState.isDragging && dragState.dragType === 'pan' ? 'grabbing' :
                  dragState.isDragging ? 'grabbing' : 
                  interactionState.mode === 'adding-node' ? 'crosshair' :
                  interactionState.mode === 'adding-edge-select-first' ? 'pointer' :
                  interactionState.mode === 'adding-edge' ? 'pointer' :
                  'grab',
          outline: 'none'
        }}
      >
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#ddd" strokeWidth="1"/>
          </pattern>
        </defs>
        
        {/* Background for panning */}
        <rect 
          id="background"
          width="100%" 
          height="100%" 
          fill="#f9f9f9" 
          style={{ cursor: 'grab', pointerEvents: 'none' }}
        />
        
        {/* Main content group with transform */}
        <g transform={`translate(${viewTransform.x}, ${viewTransform.y}) scale(${viewTransform.scale})`}>
        
        {/* Grid background that moves with content */}
        <rect 
          x="-1000" 
          y="-1000" 
          width="2800" 
          height="2600" 
          fill="url(#grid)"
          onContextMenu={handleCanvasRightClick}
          style={{ cursor: 'grab' }}
        />
        
        {/* Edges */}
        {edges.map(edge => {
          const fromNode = nodes.find(n => n.id === edge.from);
          const toNode = nodes.find(n => n.id === edge.to);

          if (!fromNode || !toNode) return null;

          return (
            <Edge
              key={edge.id}
              edge={edge}
              fromNode={fromNode}
              toNode={toNode}
              onMouseDown={handleEdgeMouseDown}
              onContextMenu={handleEdgeRightClick}
              dragState={dragState}
              hoverElement={hoverElement}
              onElementHover={onElementHover}
              rent={computeLineRent(edge, lmpByNodeId)}
            />
          );
        })}
        
        {/* Nodes */}
        {nodes.map(node => {
          const isSelected = interactionState.mode === 'adding-edge' && interactionState.selectedNodeForEdge === node.id;
          const isDragging = dragState.isDragging && dragState.nodeId === node.id;
          
          return (
            <Node
              key={node.id}
              node={node}
              isSelected={isSelected}
              isBusSelected={selectedBusId === node.id}
              isDragging={isDragging}
              onMouseDown={combinedNodeMouseDown}
              onContextMenu={handleNodeRightClick}
              interactionState={interactionState}
              dragState={dragState}
              hoverElement={hoverElement}
              onElementHover={onElementHover}
              batteryStates={batteryStates}
            />
          );
        })}
        
        </g> {/* End main content group with transform */}
      </svg>

      {contextMenu.show && (
        <ContextMenu
          anchor={{ x: contextMenu.x, y: contextMenu.y }}
          items={contextMenuItems()}
          onClose={handleContextMenuClose}
        />
      )}

      {/* Auto Layout - Bottom Right, below zoom controls */}
      <button
        onClick={onOptimizeLayout}
        title="Auto-optimize layout"
        style={{
          position: 'absolute',
          right: '20px',
          bottom: '20px',
          zIndex: 100,
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          border: '2px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '18px',
          color: '#333',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          outline: 'none',
          userSelect: 'none',
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fff';
        }}
      >
        <span role="img" aria-label="auto layout">🪄</span>
      </button>

      {/* Zoom Controls - Bottom Right */}
      <ZoomControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomToFit={handleZoomToFitClick}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
      />
    </div>
  );
}

export default GridVisualization;
